/**
 * AddSyncPage - "Add sync" for an already-connected source.
 *
 * Replaces the old 4-step Create Job wizard. It re-introspects the existing
 * source (using its stored credentials) and reuses the exact table-picker the
 * Connect flow uses: pick tables, set frequency, choose history, done.
 *
 * For non-tabular sources (REST / Kafka / Webhook) there's nothing to map, so
 * we show a short note and point the user at the classic field-mapping wizard
 * for those advanced cases.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Stack, Chip, Button, CircularProgress, Alert,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import { ConnectorLogo, computeBackfillLimits, scopeToBackfill } from './connectorMeta';
import TableSyncPicker from './TableSyncPicker';
import {
  fetchRequest as inspectSourceRequest,
  clear as clearInspectSource,
} from '../../redux/actions/datasources/inspectSource';
import {
  fetchRequest as addJobsRequest,
  clear as clearAddJobs,
} from '../../redux/actions/datasources/addJobs';

const TABLE_TYPES = ['postgres', 'postgresql', 'mysql', 'sqlserver', 'bigquery'];

const EMPTY_SLICE = { response: null, isLoading: false, error: null };

export default function AddSyncPage() {
  const { sourceId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [tables, setTables] = useState([]);
  const [selected, setSelected] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [frequency, setFrequency] = useState('hourly');
  const [importHistory, setImportHistory] = useState(true);
  const [plan, setPlan] = useState(null);
  const [fitToPlan, setFitToPlan] = useState(true);
  const [scopes, setScopes] = useState({});
  const [done, setDone] = useState(false);

  const inspectSlice = useSelector((s) => s.datasource_inspect_source) || EMPTY_SLICE;
  const addJobsSlice = useSelector((s) => s.datasource_add_jobs) || EMPTY_SLICE;
  const creating = addJobsSlice.isLoading;

  useEffect(() => {
    dispatch(inspectSourceRequest({ sourceId }));
    return () => {
      dispatch(clearInspectSource());
      dispatch(clearAddJobs());
    };
  }, [sourceId, dispatch]);

  useEffect(() => {
    const data = inspectSlice.response;
    if (!data) return;
    if (data.status === 'failed' || data.test?.success === false) {
      setError(data.test?.error || 'Could not connect to this source.');
      setResult(data);
    } else {
      setResult(data);
      setPlan(data.plan || null);
      const tbls = data.tables || [];
      setTables(tbls);
      const sel = {};
      tbls.forEach((t) => { sel[t.table_name] = !!t.timestamp_column; });
      setSelected(sel);
    }
    setLoading(false);
  }, [inspectSlice.response]);

  useEffect(() => {
    if (inspectSlice.error) {
      setError(inspectSlice.error);
      setLoading(false);
    }
  }, [inspectSlice.error]);

  const updateColumn = (tableName, idx, patch) => {
    setTables((prev) => prev.map((t) => {
      if (t.table_name !== tableName) return t;
      const columns = t.columns.map((c, i) => {
        if (i !== idx) {
          if (patch.is_timestamp) return { ...c, is_timestamp: false };
          if (patch.is_entity) return { ...c, is_entity: false };
          return c;
        }
        return { ...c, ...patch };
      });
      const ts = columns.find((c) => c.is_timestamp);
      const ent = columns.find((c) => c.is_entity);
      return { ...t, columns, timestamp_column: ts?.source_field || null, entity_column: ent?.source_field || null };
    }));
  };

  const submit = useCallback(() => {
    setError(null);
    const remaining = plan?.signals_remaining;
    const limits = (importHistory && fitToPlan && remaining != null)
      ? computeBackfillLimits(tables, selected, remaining) : {};
    const chosen = tables.filter((t) => selected[t.table_name]).map((t) => ({
      table_name: t.table_name, frequency,
      timestamp_column: t.timestamp_column, entity_column: t.entity_column,
      watermark_column: t.watermark_column, watermark_type: t.watermark_type,
      ...(importHistory ? scopeToBackfill(scopes[t.table_name], limits[t.table_name]) : {}),
      field_mappings: t.columns.filter((c) => c.included).map((c) => ({
        source_field: c.source_field, target_key: c.target_key, data_type: c.data_type,
        display_name: c.display_name || undefined,
        is_timestamp: c.is_timestamp, is_entity: c.is_entity, is_event: c.is_event || false, parse_options: {},
      })),
    }));
    dispatch(addJobsRequest({ sourceId, tables: chosen, import_historical: importHistory }));
  }, [tables, selected, frequency, importHistory, plan, fitToPlan, scopes, sourceId, dispatch]);

  useEffect(() => {
    if (!addJobsSlice.response) return;
    dispatch(clearAddJobs());
    setDone(true);
    setTimeout(() => navigate(`/datasources/${sourceId}`), 1200);
  }, [addJobsSlice.response, dispatch, navigate, sourceId]);

  useEffect(() => {
    if (addJobsSlice.error) {
      setError(addJobsSlice.error);
      dispatch(clearAddJobs());
    }
  }, [addJobsSlice.error, dispatch]);

  const Header = (
    <PageBreadcrumbs items={[
      { text: 'Data Sources', link: '/datasources', icon: <StorageIcon /> },
      { text: result?.label || 'Source', link: `/datasources/${sourceId}` },
      { text: 'Add sync' },
    ]} />
  );

  if (loading) {
    return (
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>{Header}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 6, justifyContent: 'center' }}>
          <CircularProgress size={20} /><Typography color="text.secondary">Reading schema…</Typography>
        </Stack>
      </Box>
    );
  }

  if (done) {
    return (
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>{Header}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main' }} />
          <Typography variant="h6" sx={{ mt: 1.5, fontWeight: 700 }}>Sync added</Typography>
          <CircularProgress size={16} sx={{ mt: 2 }} />
        </Box>
      </Box>
    );
  }

  const isTabular = TABLE_TYPES.includes(result?.connector_type);
  const selectedCount = Object.values(selected).filter(Boolean).length;
  // A time column is mandatory on every selected source.
  const missingTimestamp = tables.some((t) => selected[t.table_name] && !t.timestamp_column);

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto' }}>
      {Header}
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
        <ConnectorLogo type={result?.connector_type} size={28} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{result?.label || 'Add sync'}</Typography>
        {result?.test?.success && <Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} label="Connected" />}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {!isTabular ? (
        <Alert severity="info" action={
          <Button color="inherit" size="small" onClick={() => navigate(`/datasources/${sourceId}/jobs/create`)}>Open field mapping</Button>
        }>
          {result?.connector_type ? `${result.connector_type.toUpperCase()} sources` : 'This source'} use a custom field mapping. Use the advanced setup to define what to pull.
        </Alert>
      ) : (
        <>
          <TableSyncPicker
            tables={tables} selected={selected} setSelected={setSelected}
            expanded={expanded} setExpanded={setExpanded}
            frequency={frequency} setFrequency={setFrequency}
            importHistory={importHistory} setImportHistory={setImportHistory}
            updateColumn={updateColumn}
            plan={plan} fitToPlan={fitToPlan} setFitToPlan={setFitToPlan}
            scopes={scopes} setScope={(name, patch) => setScopes((s) => ({ ...s, [name]: { ...s[name], ...patch } }))}
          />
          {missingTimestamp && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Every source needs a time column. Open <strong>Configure</strong> and tap <strong>time</strong> on the timestamp column.
            </Alert>
          )}
          <Stack direction="row" spacing={2} sx={{ mt: 3 }} justifyContent="space-between">
            <Button onClick={() => navigate(`/datasources/${sourceId}`)} disabled={creating} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" disabled={selectedCount === 0 || creating || missingTimestamp} onClick={submit}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <BoltIcon />} sx={{ textTransform: 'none', px: 3 }}>
              {creating ? 'Adding…' : `Add sync for ${selectedCount} table${selectedCount === 1 ? '' : 's'}`}
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}
